import { apiRequest } from '../api/client'
import type { DeviceCommand, DeviceReportItem, DeviceReportPayload } from '../api/types'
import { listOutbox, type OutboxRecord } from './db'
import { clearOutbox, summarizeQueue, type OutboxClearScope } from './outbox'
import type { SyncProgress, SyncSession } from './sync'

/**
 * Удалённая диагностика автономной работы. Приложение периодически отправляет на сервер отчёт о состоянии устройства
 * (версия сборки и оболочки, очередь и её застрявшие действия, хранилище) и получает команды администратора:
 * очистить очередь или перезапустить приложение. Так проблемы с офлайн-режимом видны и решаются без доступа к телефону.
 */
const DEVICE_KEY = 'amalthea.device.v1'
const EXECUTED_KEY = 'amalthea.device.commands.v1'
const EXECUTED_LIMIT = 50
export const REPORT_ITEM_LIMIT = 200
export const APP_BUILD: string = typeof __APP_BUILD__ === 'string' ? __APP_BUILD__ : 'dev'

function readList(key: string): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) || '[]')
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : []
  } catch {
    return []
  }
}

/** Постоянный идентификатор установки приложения в этом браузере (не привязан к учётной записи). */
export function deviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_KEY)
    if (existing && /^[A-Za-z0-9-]{8,64}$/.test(existing)) return existing
    const created = crypto.randomUUID()
    localStorage.setItem(DEVICE_KEY, created)
    return created
  } catch {
    return 'unknown-device'
  }
}

/** Версия сохранённой офлайн-оболочки (из служебного кеша сервис-воркера) или null, если оболочки нет. */
export async function shellVersion(): Promise<string | null> {
  if (typeof caches === 'undefined') return null
  try {
    const metadata = await caches.open('amalthea-shell-metadata')
    const marker = await metadata.match('/__pwa/active-shell')
    if (!marker) return null
    const versions = await marker.json() as { active?: unknown }
    return typeof versions.active === 'string' ? versions.active.replace('amalthea-shell-', '') : null
  } catch {
    return null
  }
}

export function platformLabel(userAgent = navigator.userAgent): string {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'iOS'
  if (/Android/i.test(userAgent)) return 'Android'
  if (/Windows/i.test(userAgent)) return 'Windows'
  if (/Mac OS X/i.test(userAgent)) return 'macOS'
  if (/Linux/i.test(userAgent)) return 'Linux'
  return 'other'
}

export function reportItem(record: OutboxRecord): DeviceReportItem {
  return {
    id: record.id,
    label: record.label.slice(0, 200),
    method: record.method,
    path: record.path.replace(/\?.*$/, '').slice(0, 300),
    state: record.state,
    attempts: record.attempts,
    createdAt: new Date(record.createdAt).toISOString(),
    updatedAt: new Date(record.updatedAt).toISOString(),
    responseStatus: record.responseStatus ?? null,
    errorCode: record.errorCode ?? null,
    lastError: record.lastError ? record.lastError.slice(0, 300) : null,
    fileSize: record.fileSize ?? (record.fileBlob ? record.fileBlob.size : null),
  }
}

export interface ReportContext {
  records?: OutboxRecord[]
  lastSyncAt?: number | null
  lastResult?: SyncProgress | null
  executedCommands?: string[]
  swState?: string | null
}

export async function buildDeviceReport(userId: string, context: ReportContext = {}): Promise<DeviceReportPayload> {
  const records = context.records ?? await listOutbox(userId)
  let storage: StorageEstimate | undefined
  try { storage = await navigator.storage?.estimate?.() } catch { storage = undefined }
  let swState: string | null = context.swState ?? null
  if (swState === null && 'serviceWorker' in navigator) {
    try { swState = (await navigator.serviceWorker.getRegistration())?.active ? 'ready' : 'missing' } catch { swState = 'unknown' }
  }
  // Застрявшие действия — первыми, чтобы они попали в отчёт даже при длинной очереди.
  const ordered = [...records].sort((left, right) => Number(isProblem(right)) - Number(isProblem(left)) || left.createdAt - right.createdAt)
  return {
    deviceId: deviceId(),
    appBuild: APP_BUILD,
    shellVersion: await shellVersion(),
    userAgent: navigator.userAgent.slice(0, 500),
    platform: platformLabel(),
    standalone: typeof window.matchMedia === 'function' ? window.matchMedia('(display-mode: standalone)').matches : false,
    online: navigator.onLine,
    swState,
    storageUsed: storage?.usage ?? null,
    storageQuota: storage?.quota ?? null,
    timezone: safeTimezone(),
    queue: summarizeQueue(records),
    items: ordered.slice(0, REPORT_ITEM_LIMIT).map(reportItem),
    lastSyncAt: context.lastSyncAt ? new Date(context.lastSyncAt).toISOString() : null,
    lastSyncResult: context.lastResult ?? null,
    executedCommands: Array.from(new Set([...(context.executedCommands ?? []), ...readList(EXECUTED_KEY)])).slice(0, EXECUTED_LIMIT),
    reportedAt: new Date().toISOString(),
  }
}

function isProblem(record: OutboxRecord): boolean {
  return ['auth-required', 'conflict', 'failed', 'obsolete'].includes(record.state) || record.attempts >= 8
}

function safeTimezone(): string | null {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null } catch { return null }
}

export async function sendDeviceReport(session: SyncSession, report: DeviceReportPayload): Promise<DeviceCommand[]> {
  const response = await apiRequest<{ commands?: unknown }>('/sync/report', {
    method: 'POST',
    body: report,
    authToken: session.token,
    notifyOnUnauthorized: false,
    timeout: 20_000,
  })
  const commands = response && typeof response === 'object' ? response.commands : undefined
  return Array.isArray(commands) ? commands.filter((command): command is DeviceCommand => Boolean(command) && typeof command === 'object' && typeof (command as DeviceCommand).id === 'string' && typeof (command as DeviceCommand).type === 'string') : []
}

export function rememberExecuted(ids: string[]): void {
  if (ids.length === 0) return
  try {
    localStorage.setItem(EXECUTED_KEY, JSON.stringify(Array.from(new Set([...ids, ...readList(EXECUTED_KEY)])).slice(0, EXECUTED_LIMIT)))
  } catch {
    // Без localStorage команда просто выполнится ещё раз: очистка идемпотентна, перезапуск сервер снимает после подтверждения.
  }
}

export interface CommandOutcome {
  executed: string[]
  cleared: number
  reload: boolean
}

/** Выполняет команды сервера для очереди этого пользователя; уже выполненные (по памяти устройства) не повторяются. */
export async function applyDeviceCommands(userId: string, commands: DeviceCommand[]): Promise<CommandOutcome> {
  const done = new Set(readList(EXECUTED_KEY))
  const outcome: CommandOutcome = { executed: [], cleared: 0, reload: false }
  for (const command of commands) {
    if (done.has(command.id)) { outcome.executed.push(command.id); continue }
    if (command.type === 'clear-outbox') {
      const scope: OutboxClearScope = command.scope === 'all' || command.scope === 'obsolete' ? command.scope : 'stuck'
      const before = command.before ? Date.parse(command.before) : Number.NaN
      outcome.cleared += await clearOutbox(userId, scope, Number.isFinite(before) ? before : undefined)
      outcome.executed.push(command.id)
    } else if (command.type === 'reload') {
      outcome.reload = true
      outcome.executed.push(command.id)
    }
  }
  rememberExecuted(outcome.executed)
  return outcome
}
